import React, { useCallback, useEffect, useRef, useState } from "react";
import GetAppIcon from '@material-ui/icons/GetApp';
import { Chart } from "react-google-charts";
import { colors } from "../../helpers/colors";
import { defaultTextImage } from "../../configuration/variables";

const defaultColors = [colors.azul, colors.celeste, colors.amarillo];
const ChartArea = ({ 
  data, 
  colors=[], 
  subTitle="", 
  sort = false, 
  referencias = {},
  chartConfig={
    height: '100%',
    chartArea: { width: "85%", height: '' }
  },
  chartComment=null,
  chartCommentJsx=null
}) => {
  const imageRef = useRef();
  const canvasRef = useRef();
  const [columns, setColumns] = useState([])
  const [imageUrl, setImageUrl] = useState('');
  const imageResolution = { width: 1366, height: 768 }

  const applyDataFormat = useCallback(() => {
    let allColumns = [];
    let allColumnsCopy = [];

    let headersColumns = ['valores']
    let headersColumnsRef = ['valores']
    // obtener titulos
    data.forEach(item => {
      const headerSaved = headersColumnsRef.find(header => header === item.LABEL);
      if( !headerSaved ) {
        headersColumns = [ ...headersColumns, `% neto ${item.LABEL}`, {role:'annotation'} ]
        headersColumnsRef = [ ...headersColumnsRef, item.LABEL ]
      }
    });

    for (let index = 0; index < data.length; index++) {
      if(allColumns.length === 0){
        let currentColumn = [];
        const iterableObj = Object.entries(data[index]);
        for (const currentItem of iterableObj) {
          const [ key, value ] = currentItem;
          currentColumn = [ ...currentColumn, [key, Math.round(value), `${Math.round(value)}%` ] ]
        }
        allColumns = [ ...allColumns, ...currentColumn ]
      }else{
        let columnsToReplace = []
        const iterableObj = Object.entries(data[index]);
        for (let subIndex = 0; subIndex < allColumns.length; subIndex++) {
          const element = allColumns[subIndex]; // ["INGRESOS_POS", 9.1, 9.1] 
          const valueAdd = iterableObj[subIndex][1];
          columnsToReplace = [ ...columnsToReplace, [ ...element, Math.round(valueAdd), `${Math.round(valueAdd)}%` ] ]
        }
        allColumns = columnsToReplace;
        allColumnsCopy = columnsToReplace;
      }
    }
    
    const columnsChart = allColumnsCopy.length === 0 ? allColumns : allColumnsCopy;
    const columnsSaved = columnsChart.filter(column => column[0] !== 'LABEL') 
    const newColumnsWithHeaders = replaceHeadersWithReference([...columnsSaved]);
    
    if(sort){
      const sorted = sortColumns([...newColumnsWithHeaders]);
      return setColumns([headersColumns, ...sorted]);
    }
    setColumns([headersColumns, ...newColumnsWithHeaders]);
  }, [data]);

  const replaceHeadersWithReference = ( arr = [] ) => {
    const listado = [];
    arr.forEach(item => {
      const indicador = item[0];
      const elementIndicador = referencias[indicador];
      if(elementIndicador){
        const valuesIndicador = item.splice(1, item.length);
        listado.push([ ...elementIndicador, ...valuesIndicador ]);
      }
    });
    return listado;
  }

  const sortColumns = (arr) => {
    const length = arr.length; 
    const spliced = arr.splice(0, length);
    return spliced.sort((a, b) => b[1] - a[1]);
  }

  const createImageWithText = () => {
    // generar la imagen con el texto
    const currentCanvas = canvasRef.current;
    const ctx = currentCanvas.getContext("2d");
    ctx.drawImage(imageRef.current, 0, 60);
    ctx.font = "11px Arial";
    ctx.textAlign = "center";   
    ctx.fillText(defaultTextImage, imageResolution.width / 2, 75); 

    // descagr la imagen
    let a = document.createElement("a");
    let imageUrl = currentCanvas.toDataURL("image/png");
    a.href = imageUrl;
    a.target = "_blank";
    a.download = "grafico.png";
    a.click();
  }

  useEffect(() => {
    setImageUrl(''); // cada vez que se el formato cambie, se actualizará la imagen de el gráfico.
    applyDataFormat();
  }, [data])

  return (
    <div className="col-xl-12 col-md-12 col-xs-12">
      <div className="div-char">
        <div className="d-flex justify-content-between align-items-center">
          <h6>{subTitle}</h6>
          <button
            className="btn btn-sm fs-3" 
            onClick={ createImageWithText }
            style={{ background: '#53565A', color: 'white' }}
            title="Descargar gráfico (png)"
          >
            <GetAppIcon/>
          </button>
        </div>
        <div style={{ display: 'none' }}>
          <canvas
            ref={ canvasRef } 
            width={ imageResolution.width } 
            height={ imageResolution.height }/>
          <img
            ref={ imageRef }
            alt="Gráficos"
            src={ imageUrl }
          />
        </div>

        <div style={{ position: 'relative', }} >
          <div style={{ position: 'absolute', visibility: 'hidden', left: '-1400px', bottom: '-100px' }} >
            <Chart
              width={`${imageResolution.width}px`}
              height={`${imageResolution.height}px`}
              chartType="LineChart"
              data={columns}
              loader={<div>Cargando</div>}
              chartEvents={[
                {
                  eventName: 'ready',
                  callback: (ChartRef) => {
                    if(!imageUrl) {
                      setImageUrl(ChartRef.chartWrapper.getChart().getImageURI());
                    }
                  },
                },
              ]}
              options={{
                chartArea: { top: 50 },
                annotations: {
                  textStyle: {
                    colors: colors.length === 0 ? defaultColors : colors,
                    fontSize: 13,
                  },
                  alwaysOutside: true
                },
                height: 500,
                legend: { 
                  position: "top", 
                  alignment: "center",
                  textStyle: {
                    fontSize: 13,
                  },
                },
                colors: colors.length === 0 ? defaultColors : colors,
                hAxis: {
                  minValue: 0,
                  textStyle: {
                    fontSize: 13,
                  },
                },
              }}
              // For tests
              rootProps={{ "data-testid": "3" }}
            />
          </div>
        </div>

        <Chart
          height={chartConfig.height}
          chartType="LineChart"
          loader={<div>Loading Chart</div>}
          data={columns}
          // options={{
          //   isStacked: true,
          //   height: 300,
          //   legend: { position: "top", maxLines: 3 },
          //   vAxis: { minValue: 0 },
          // }}
          options={{
            chartArea: { 
              top: 50,
              width: chartConfig.chartArea.width, 
              height: chartConfig.chartArea.height,  
            },
            annotations: {
              textStyle: {
                colors: colors.length === 0 ? defaultColors : colors,
                fontSize: 13,
              },
              alwaysOutside: true
            },
            height: 500,
            legend: { 
              position: "top", 
              alignment: "center",
              textStyle: {
                fontSize: 13,
              },
            },
            colors: colors.length === 0 ? defaultColors : colors,
            hAxis: {
              minValue: 0,
              textStyle: {
                fontSize: 13,
              },
            },
          }}
        />
        {chartComment && <p className="text-muted mb-0">{chartComment}</p>}
        {chartCommentJsx && chartCommentJsx}
      </div>
    </div>
  );
};

export default ChartArea;
